import React, { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useLazyQuery } from '@apollo/client'
import qs from 'query-string'
import shuffle from 'lodash.shuffle'
import { useDebounce } from 'use-debounce'
import { QUERY } from '../../../graphql'
import { InputSearch } from './InputSearch'
import { GradCard } from '../../../components/2025/GradCard'
import { ProjectCard } from '../../../components/2025/ProjectCard'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { SpringContext } from '../../../context/SpringContext'
import { ThemeContext } from '../../../context/2025/ThemeContext'
import { useViewport } from '../../../hook/useViewport'

const Header = React.lazy(() => import('../../../components/2025/Header'))
const Footer = React.lazy(() => import('../../../components/2025/Footer'))
const FullScreenLoader = React.lazy(() => import('../../../components/FullScreenLoader'))

export const TextCenterDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  padding: 120px 0 0px 0;
`

export default function Search(props) {
  const history = useHistory()
  const location = useLocation()
  const { search: searchParams } = location
  const { keyword } = qs.parse(searchParams)
  const [searchInput, setSearchInput] = useState(keyword || '')
  const [searchInputDebounce] = useDebounce(searchInput, 800)
  const spring = useContext(SpringContext)
  const { isDarkMode } = useContext(ThemeContext)
  const inputRef = useRef(null)
  const { width } = useViewport()
  const isMobile = width <= 640

  // --- Step 1: Add a ref to track if a search was explicitly triggered ---
  const hasSearchBeenTriggered = useRef(false);

  const [fetchSearch, { data: searchData, loading }] = useLazyQuery(QUERY.SEARCH_QUERY, {
    variables: {
      q: searchInputDebounce,
      year: 2025,
    },
    // Optional: Reset trigger ref if a new query starts
    // onCompleted: () => {},
    // onError: () => { hasSearchBeenTriggered.current = false; } // Also reset on error if needed
  })

  const onInputChange = useCallback((e) => {
    const val = e.target.value
    setSearchInput(val)
    // Don't trigger fetchSearch or set the ref here, let the debounce handle it
  }, [])

  const onSearchClick = () => {
    const val = searchInput
    setSearchInput(val) // Update state (debounce will pick it up)
    if (val && val.trim().length > 0) {
        // --- Step 2: Mark search as triggered on explicit click ---
        hasSearchBeenTriggered.current = true;
        fetchSearch(); // Trigger search immediately on click
    }
  }

  // Effect to update URL based on debounced input
  useEffect(() => {
    if (!searchInputDebounce || searchInputDebounce?.length === 0) {
      // Only replace history if the current path isn't already '/search' without params
      if (location.pathname !== '/search' || location.search) {
           history.replace('/search');
      }
      return
    }
    // Only push history if the keyword actually changed
    if (searchInputDebounce !== keyword) {
        history.push({ search: `?keyword=${searchInputDebounce}` });
    }
  }, [searchInputDebounce, history, location.pathname, location.search, keyword]) // Added dependencies


  // Effect to fetch search results when debounced input changes (or initial keyword loads)
  useEffect(() => {
    // Only search if there's a valid query from debounced input or initial URL keyword
    const queryToSearch = searchInputDebounce || keyword; // Use debounced input primarily
    if (queryToSearch && queryToSearch.trim().length > 0) {
      // --- Step 2: Mark search as triggered when debounce fires ---
      // Ensure we only set this if the input isn't empty
      if (searchInputDebounce && searchInputDebounce.trim().length > 0) {
         hasSearchBeenTriggered.current = true;
      }
      fetchSearch({ variables: { q: queryToSearch.trim(), year: 2025 } }); // Ensure using the correct query
    } else {
      // If input becomes empty, reset the trigger flag
      hasSearchBeenTriggered.current = false;
    }
    // Dependency array includes keyword to fetch on initial load if URL has it
  }, [keyword, searchInputDebounce, fetchSearch])


  // Effect to reset Spring animation (seems unrelated to focus, keep as is)
  useEffect(() => {
    if (spring != null && spring !== undefined) {
      spring.setCurrentValue(0).setAtRest()
      spring.setEndValue(0)
    }
  }, [spring])


  // --- Step 3: Modify the focus effect ---
  useEffect(() => {
    // Only focus if:
    // 1. Loading has finished (!loading)
    // 2. The input ref exists (inputRef.current)
    // 3. A search was actually triggered before this loading state change (hasSearchBeenTriggered.current)
    if (!loading && inputRef.current && hasSearchBeenTriggered.current) {
      // Double RAF to ensure all DOM updates complete before focusing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current.focus({ preventScroll: true });
          // --- Reset the trigger flag after focusing ---
          hasSearchBeenTriggered.current = false;
        });
      });
    } else if (!loading && !hasSearchBeenTriggered.current) {
        // Optional: Explicitly blur if loading is done but no search was triggered
        // This can prevent browsers from auto-focusing in some edge cases.
        // if (document.activeElement === inputRef.current) {
        //     inputRef.current.blur();
        // }
    }
  }, [loading]); // Only depends on the loading state


  const results = useMemo(() => {
    // Return empty array if no search query being actively searched
    const queryInEffect = searchInputDebounce || keyword; // Consider both debounced and initial URL keyword
    if (!queryInEffect || queryInEffect.trim().length === 0) return []

    const users = searchData?.searchGradWork?.users || []
    const projects = searchData?.searchGradWork?.projects || []
    // Shuffle only if there are results to avoid unnecessary computation
    if (users.length > 0 || projects.length > 0) {
        return shuffle([...users, ...projects])
    }
    return []; // Return empty if no users or projects found
  }, [searchData, searchInputDebounce, keyword]) // Added keyword dependency

  const backgroundColors = {
    header: isDarkMode ? 'bg-black' : 'bg-white',
    content: isDarkMode ? 'bg-[#222222]' : 'bg-black',
    footer: isDarkMode ? 'bg-black' : 'bg-black',
  }

  const textColors = {
    heading: isDarkMode ? 'text-white' : 'text-lavender',
    subheading: isDarkMode ? 'text-white' : 'text-black',
    body: isDarkMode ? 'text-white' : 'text-black',
  }

  require('./styles.scss')

  // Determine if we should show the "no results" message
  const showNoResults = !loading && (searchInputDebounce || keyword) && searchInputDebounce?.trim().length > 0 && results.length === 0;
  // Determine if we should show the "enter search" prompt
  const showPrompt = !loading && (!searchInputDebounce || searchInputDebounce.trim().length === 0) && results.length === 0;


  return (
    <div
      className={`absolute w-full min-h-full bg-black flex flex-col items-center justify-start pb-20 lg:pb-0  ${
        isDarkMode ? 'bg-black' : 'bg-white'
      } transition-colors duration-300 `}
    >
      {/* Keep FullScreenLoader logic */}
      {loading && (
        <FullScreenLoader
          color={isDarkMode ? `#DAFF01` : `#9167F0`}
          style={{ position: 'fixed' }}
        />
      )}
      {Header && <Header />}

      <section
        className={`w-full ${
          isDarkMode ? 'bg-black' : 'bg-white'
        } transition-colors duration-300 flex-1`}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-32 flex flex-col items-center justify-center gap-8'>
          <TextCenterDiv className=' px-[16px] lg:px-[48px]'>
            <h1
              className={`${isMobile ? 'heading-1-mobile' : 'heading-1'} ${
                isDarkMode ? 'text-lime' : 'text-lavender'
              } `}
              style={{ pointerEvents: 'none' }}
            >
              Search
            </h1>
          </TextCenterDiv>
          <div className='text-center relative my-8 w-full lg:w-2/3'>
            <InputSearch
              ref={inputRef}
              type='text'
              id='search'
              name='search'
              placeholder='Search the website'
              className='w-full'
              onChange={onInputChange}
              onSearchClick={onSearchClick} // Keep this prop
              value={searchInput}
              isDarkMode={isDarkMode}
              // Ensure no autoFocus={true} prop is present here or in InputSearch definition
            />
          </div>
          {/* Show prompt message */}
           {showPrompt && (
             <h4 className={`${textColors.body} heading-4 relative text-center my-8`}>
               Please enter a student name or project name to search
             </h4>
           )}

           {/* Show no results message */}
           {showNoResults && (
             <h4 className={`${textColors.body} relative text-center my-8`}>
               No results found, let's try again!
             </h4>
           )}
        </div>
      </section>

        {/* Results Section - Conditionally render based on results length */}
        {(results && results.length > 0) && (
          <section
            className={`w-full ${
              isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
            } transition-colors duration-300 `}
          >
            <div
              className={`max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 flex flex-col items-center justify-between gap-16`}
              >
                 {/* Simplified Results Count Display */}
                 <p className='body-text text-white w-full text-left !mt-16 px-[8px]'>
                   {results.length === 1 ? (
                     <>There is a <span className='text-lime'>single</span> result.</>
                   ) : (
                     <>There are <span className='text-lime'>{results.length}</span> results.</>
                   )}
                 </p>


              <TransitionGroup
                 className={`grid grid-cols-1 lg:grid-cols-3 gap-16 px-[16px]  lg:px-[8px] pt-2 pb-16 ${
                    isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
                 }`}
              >
                {results.map((result) => (
                  <CSSTransition key={result.id} timeout={300} classNames='item'>
                    <>
                      {result.__typename === 'User' && (
                        <GradCard grad={result} isDarkMode={isDarkMode} />
                      )}
                      {result.__typename === 'Project' && (
                        <ProjectCard project={result} isDarkMode={isDarkMode} />
                      )}
                    </>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </div>
          </section>
        )}


      {/* Footer Section */}
      <div
        className={`relative w-full flex flex-col items-center justify-center w-full ${
          isDarkMode ? 'bg-black' : 'bg-light-grey-25'
        }`}
      >
        <div
          className={`relative bottom-0 w-full pt-16 px-[16px] lg:px-[48px] bg-black max-w-[1440px] ${
            isDarkMode ? 'bg-black' : 'bg-light-grey-25'
          }`}
        >
          {Footer && <Footer />}
        </div>
      </div>
    </div>
  )
}